/** @jsxImportSource @emotion/react */
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../core/redux/IAppState";
import { formatDate } from "../language/time";
import { getResource } from "../language/resources/getResource";
import { BLUE_CREEK } from "../styles/colors";
import { useCallback, useEffect, useMemo } from "react";
import { getUserSearchHistoryAction } from "./getUserSearchHistoryAction";
import { GUEST_SEARCH_ROUTE } from "../core/routes";
import { BigNeutral } from "../forms/elements/buttons";
import { clearSearchHistoryAction } from "./clearUserSearchHistoryAction";
import { ACTION_TYPE } from "../core/redux/actionType";
import { getLoading } from "../core/redux/api/module";
import { css } from "@emotion/react";
import { useNavigate } from "react-router";

const searchHistoryTitle = css`
  display: block;
  text-align: center;
  font-size: 22px;
  font-weight: bold;
  margin-top: 20px;
`;

const searchHistoryOuterContainer = css`
  display: flex;
  flex-wrap: wrap;
  width: 1040px;
  justify-content: flex-start;
  margin: auto;
  margin-bottom: 20px;
`;

const searchHistoryContainer = css`
  margin-left: 5px;
  margin-right: 5px;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  text-align: center;
  width: 250px;
  border: 5px solid ${BLUE_CREEK};
  border-radius: 10px;
  padding-top: 5px;
  padding-bottom: 5px;
  font-size: 16px;
  background-color: white;

  &:hover {
    background-color: lightblue;
  }
`;

const searchHistoryItemCss = css`
  display: inline-block;
  margin-top: 3px;
  margin-bottom: 3px;
  overflow-wrap: break-word;
`;

type SearchHistoryItem = {
  searchTerm: string;
  searchUtcDateTime: string | Date;
  results: number;
};

export const SearchHistory = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const searchHistory = useSelector(
    (state: IAppState) => state.userSearchHistory
  ) as SearchHistoryItem[];

  const resources = useSelector((state: IAppState) => state.locale);
  const dateFormat = useSelector((state: IAppState) => state.defaultDateFormat);
  const resortId = useSelector((state: IAppState) => state.selectedResort.resortId);
  const isLoading = useSelector((state: IAppState) =>
    getLoading(
      state,
      ACTION_TYPE.SEARCH_HISTORY_REQUEST,
      ACTION_TYPE.SEARCH_HISTORY_REQUEST
    )
  );

  // Sort by date DESC
  const searchHistorySorted = useMemo(
    () =>
      [...searchHistory].sort(
        (a, b) =>
          new Date(b.searchUtcDateTime).getTime() -
          new Date(a.searchUtcDateTime).getTime()
      ),
    [searchHistory]
  );

  // Deduplicate search terms
  const uniqueByTerm = useMemo(() => {
    const seen = new Set<string>();
    const unique: SearchHistoryItem[] = [];
    for (const item of searchHistorySorted) {
      if (!seen.has(item.searchTerm)) {
        seen.add(item.searchTerm);
        unique.push(item);
      }
    }
    return unique;
  }, [searchHistorySorted]);

  const hasHistory = uniqueByTerm.length > 0;

  const onGuestSearched = useCallback((query: string) => {
    navigate(`${GUEST_SEARCH_ROUTE}/${encodeURIComponent(query)}`);
    }, [navigate]
  );

  const clearSearchHistory = useCallback(() => {
    dispatch(clearSearchHistoryAction(resortId));
  }, [dispatch, resortId]);

  useEffect(() => {
    dispatch(getUserSearchHistoryAction(resortId));
  }, [dispatch, resortId]);

  return (
    <div>
      {hasHistory && !isLoading && (
        <div>
          <span css={searchHistoryTitle}>
            {getResource(resources, "SEARCH_HISTORY_TITLE")}
          </span>
          <div css={searchHistoryOuterContainer}>
            {uniqueByTerm.map((item, index) => (
              <button
                key={`${item.searchTerm}-${index}`}
                css={searchHistoryContainer}
                onClick={() => onGuestSearched(item.searchTerm)}
              >
                <div css={searchHistoryItemCss}>{item.searchTerm}</div>
                <div css={searchHistoryItemCss}>
                  {formatDate(item.searchUtcDateTime, dateFormat)}
                </div>
                <div css={searchHistoryItemCss}>
                  {getResource(resources, "SEARCH_HISTORY_RESULTS") +
                    " " +
                    item.results}
                </div>
              </button>
            ))}
          </div>
          <div css={css`text-align: center;`}>
            <BigNeutral onClick={clearSearchHistory}>
              {getResource(resources, "SEARCH_HISTORY_CLEAR")}
            </BigNeutral>
          </div>
        </div>
      )}
    </div>
  );
};
